import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { UnsubscribeService } from '@vendasta/notifications-sdk';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { ImageService } from '../core/image.service';
import { Feature, PartnerMarketConfigService } from '../partner-market-config';

interface State {
  loading?: boolean;
  error?: string;

  whitelabelEnabled?: boolean;

  unsubscribing?: boolean;
  unsubscribed?: boolean;

  token?: string;

  logoSrc?: string;

  partnerId?: string;
  marketId?: string;
  email?: string;
  typeName?: string;
}

const initialState: State = {
  loading: true,
  error: null,

  whitelabelEnabled: false,

  unsubscribing: false,
  unsubscribed: false,

  token: null,

  logoSrc: null,

  partnerId: null,
  marketId: null,
  email: null,
  typeName: null,
};

@Component({
  selector: 'bc-notifications-unsubscribe',
  styleUrls: ['./unsubscribe.component.scss'],
  templateUrl: './unsubscribe.component.html',
  standalone: false,
})
export class UnsubscribeComponent implements OnInit, OnDestroy {
  private readonly store: BehaviorSubject<State> = new BehaviorSubject(initialState);

  public readonly loading$: Observable<boolean> = this.store.pipe(
    map((state) => state.loading),
    distinctUntilChanged(),
  );

  public readonly error$: Observable<string> = this.store.pipe(
    map((state) => state.error),
    distinctUntilChanged(),
  );

  public readonly hasError$: Observable<boolean> = this.error$.pipe(map((e) => Boolean(e)));

  public readonly logoSrc$: Observable<string> = this.store.pipe(
    map((state) => state.logoSrc),
    distinctUntilChanged(),
  );

  public readonly hasLogoSrc$: Observable<boolean> = this.logoSrc$.pipe(map((l) => Boolean(l)));

  public readonly unsubscribing$: Observable<boolean> = this.store.pipe(
    map((state) => state.unsubscribing),
    distinctUntilChanged(),
  );
  public readonly unsubscribed$: Observable<boolean> = this.store.pipe(
    map((state) => state.unsubscribed),
    distinctUntilChanged(),
  );
  public readonly typeName$: Observable<string> = this.store.pipe(
    map((state) => state.typeName),
    distinctUntilChanged(),
  );
  public readonly whitelabelEnabled$: Observable<boolean> = this.store.pipe(
    map((state) => state.whitelabelEnabled),
    distinctUntilChanged(),
  );

  private readonly partnerId$: Observable<string> = this.store.pipe(
    map((state) => state.partnerId),
    distinctUntilChanged(),
  );
  private readonly marketId$: Observable<string> = this.store.pipe(
    map((state) => state.marketId),
    distinctUntilChanged(),
  );

  private readonly destroyed$$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private appConfig: AppConfigService,
    private partnerMarketConfigService: PartnerMarketConfigService,
    private imageServer: ImageService,
    private snackbarService: SnackbarService,
    private unsubscribeService: UnsubscribeService,
  ) {}

  ngOnInit(): void {
    // Get the overview based off the token set on the query params.
    this.route.queryParams
      .pipe(
        tap(({ token }) => this.setState({ token })),
        switchMap(({ token }) => {
          if (!token) {
            throw new Error('user id, type id and token is required');
          }
          return this.unsubscribeService.getOverview$(token);
        }),
        map((resp) => {
          if (!resp || !resp.email || !resp.type) {
            throw new Error('email and type is required');
          }
          return resp;
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe({
        next: ({ email, type, partnerId, marketId }) =>
          this.setState({
            loading: false,
            typeName: type.name,
            // If the market id is still null, use an empty string so we know the difference between no
            // market, and not having the data.
            marketId: marketId || '',
            email,
            partnerId,
          }),
        error: (err) => {
          let message = this.translateService.instant('COMMON.GENERIC_ERROR');
          if (err.status === 401 || err.status === 403) {
            message = this.translateService.instant('NOTIFICATIONS_UNSUBSCRIBE.INVALID_LINK');
          }
          return this.setState({ loading: false, error: message });
        },
      });

    // Attach whether or not whitelabelling is enabled to the state.
    this.partnerMarketConfigService
      .hasAccessToFeature(Feature.whitelabel)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((enabled) => this.setState({ whitelabelEnabled: enabled }));

    // Try to get the whitelabelled logo for the partner and market id. If there is no information for
    // whitelabelling, or we are missing the market, we will use the partner's logo off the app config. The logo
    // will then be applied to the current state.
    const marketLogoSrc$: Observable<string> = combineLatest([this.error$, this.partnerId$, this.marketId$]).pipe(
      filter(([error, partnerId, marketId]) => (partnerId !== null && marketId !== null) || !!error),
      switchMap(([_error, partnerId, marketId]) => this.appConfig.getMarketLogoByMarket(partnerId, marketId)),
      map((src) => (src ? src.toString() : '')),
      catchError(() => of('')),
      distinctUntilChanged(),
    );
    marketLogoSrc$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$$))
      .subscribe((src) => this.setState({ logoSrc: src }));
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  private setState(update: State): void {
    const curState = this.store.getValue();
    this.store.next(Object.assign(curState, update));
  }

  public unsubscribe(): void {
    this.store
      .pipe(
        take(1),
        tap(() => this.setState({ unsubscribing: true })),
        switchMap(({ token }) => this.unsubscribeService.unsubscribe$(token)),
        catchError(() => {
          throw new Error(this.translateService.instant('COMMON.GENERIC_ERROR'));
        }),
      )
      .subscribe(
        () => this.setState({ unsubscribing: false, unsubscribed: true }),
        (err) => {
          this.setState({ unsubscribing: false });
          this.snackbarService.openErrorSnack(err.message);
        },
      );
  }
}
